/**
 * Super Admin — Workspaces
 *
 * Lists every workspace and lets the master create, rename, and delete them.
 * Delete is refused server-side when media data is linked; this page surfaces
 * that refusal, it does not pre-judge it.
 *
 * Not a security boundary — it renders for anyone who reaches /admin/workspaces,
 * and every RPC checks is_platform_master() in Postgres. No client-side guard
 * belongs here.
 *
 * @module pages/admin/Workspaces
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { YDMPageHero } from "../../components/brand";
import {
  listWorkspaces,
  createWorkspace,
  renameWorkspace,
  deleteWorkspace,
  type AdminWorkspace,
} from "../../services/adminService";

const AdminWorkspaces: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<AdminWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const result = await listWorkspaces();
    if (result.success) {
      setWorkspaces(result.data ?? []);
      setError(null);
    } else {
      setError(result.error ?? "Failed to load workspaces");
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    const result = await createWorkspace(name);
    if (result.success) {
      setNewName("");
      setError(null);
      await load(true);
    } else {
      // Keep the typed name so the master can retry, and keep the error
      // visible — reloading here would wipe it via load()'s setError(null).
      setError(result.error ?? "Failed to create workspace");
    }
    setBusy(false);
  };

  const handleRename = async (id: string) => {
    const name = renameValue.trim();
    if (!name) return;
    setBusy(true);
    const result = await renameWorkspace(id, name);
    setRenamingId(null);
    if (result.success) {
      setError(null);
      await load(true);
    } else {
      setError(result.error ?? "Failed to rename workspace");
    }
    setBusy(false);
  };

  const handleDelete = async (id: string) => {
    setBusy(true);
    const result = await deleteWorkspace(id);
    setConfirmingDeleteId(null);
    if (result.success) {
      setError(null);
      await load(true);
    } else {
      // On the block this is the RPC's own message naming the linked media; it
      // must persist, so do NOT run the reload that would clear it.
      setError(result.error ?? "Failed to delete workspace");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-ink-900 animate-spin" />
      </div>
    );
  }

  if (error && workspaces.length === 0) {
    return (
      <div className="space-y-4 animate-fade-in">
        <YDMPageHero
          eyebrow="Super Admin"
          headline="WORKSPACES"
          subtitle="Create, rename, and remove workspaces."
        />
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">
            This area is for the platform master
          </h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero
        eyebrow="Super Admin"
        headline="WORKSPACES"
        subtitle="Create, rename, and remove workspaces."
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-neutral-200 p-4 flex items-end gap-2">
        <div className="flex-1">
          <label htmlFor="new-workspace" className="block text-xs text-neutral-700 mb-1">
            New workspace
          </label>
          <input
            id="new-workspace"
            type="text"
            value={newName}
            disabled={busy}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCreate();
            }}
            placeholder="Workspace name"
            className="w-full text-sm border border-neutral-300 rounded-md px-3 py-1.5"
          />
        </div>
        <button
          type="button"
          onClick={() => void handleCreate()}
          disabled={busy || newName.trim() === ""}
          className="px-4 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-ink-950 hover:bg-primary-700 disabled:opacity-50"
        >
          Create
        </button>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-neutral-700">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Slug</th>
              <th className="px-4 py-3 font-medium">Members</th>
              <th className="px-4 py-3 font-medium">Data</th>
              <th className="px-4 py-3 font-medium sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {workspaces.map((workspace) => (
              <tr key={workspace.id}>
                <td className="px-4 py-3 text-ink-900">
                  {renamingId === workspace.id ? (
                    <input
                      type="text"
                      value={renameValue}
                      disabled={busy}
                      autoFocus
                      aria-label={`New name for ${workspace.name}`}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void handleRename(workspace.id);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      className="text-sm border border-neutral-300 rounded-md px-2 py-1"
                    />
                  ) : (
                    workspace.name
                  )}
                </td>
                <td className="px-4 py-3 text-neutral-700">{workspace.slug}</td>
                <td className="px-4 py-3 text-neutral-700">{workspace.memberCount}</td>
                <td className="px-4 py-3">
                  {workspace.hasMediaData ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs">
                      has data
                    </span>
                  ) : (
                    <span className="text-neutral-700">empty</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {renamingId === workspace.id ? (
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => void handleRename(workspace.id)}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-ink-950 hover:bg-primary-700"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setRenamingId(null)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : confirmingDeleteId === workspace.id ? (
                    <div className="inline-flex items-center gap-2">
                      <span className="text-xs text-neutral-700">Delete?</span>
                      <button
                        type="button"
                        onClick={() => void handleDelete(workspace.id)}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDeleteId(null)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setRenamingId(workspace.id);
                          setRenameValue(workspace.name);
                        }}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDeleteId(workspace.id)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminWorkspaces;
